import React, { useState } from "react";
import { Button, Chip, Stack, Typography } from "@mui/material";
import { ArrowUpRight, Plus } from "@phosphor-icons/react";
import { useFormikContext } from "formik";

import AddLinkDialog from "../../../Goals/GoalBuilderPanel/AddLinkDialog";
import SectionCard from "../../../shared/SectionCard";
import type { ResourceLink, TargetFormValues } from "../TargetBuilderPanel.types";

/**
 * Section 4 — Resources.
 * Optional list of titled URLs. Empty state shows just the "Add link" button.
 * Reuses the goal builder's AddLinkDialog for parity.
 */
const TargetFormSection4: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  const [addOpen, setAddOpen] = useState(false);

  const handleAdd = (link: ResourceLink) => {
    setFieldValue("resources", [...values.resources, link]);
  };

  const handleRemove = (index: number) => {
    setFieldValue(
      "resources",
      values.resources.filter((_, i) => i !== index)
    );
  };

  return (
    <SectionCard
      id="target-section-resources"
      title="Section 4 — Resources"
      description="Links to materials, videos, or references (optional)"
    >
      {values.resources.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {values.resources.map((res, idx) => (
            <Chip
              key={`${res.url}-${idx}`}
              icon={<ArrowUpRight size={16} weight="regular" />}
              label={res.title || res.url}
              onDelete={() => handleRemove(idx)}
              variant="outlined"
              sx={{ maxWidth: 320 }}
            />
          ))}
        </Stack>
      )}

      {values.resources.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No resources added yet.
        </Typography>
      )}

      <Button
        variant="contained"
        color="secondary"
        startIcon={<Plus size={16} />}
        onClick={(event) => {
          event.currentTarget.blur();
          setAddOpen(true);
        }}
        sx={{ alignSelf: "flex-start" }}
      >
        Add link
      </Button>

      <AddLinkDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
        entityLabel="target"
      />
    </SectionCard>
  );
};

export default TargetFormSection4;
